import { firstValueFrom } from 'rxjs';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

import { IInvoiceInfo, IUserCompany, ngHelper } from '@cakeiteasy/web-utils';

import { IUser } from '../../../users';
import { IPaymentInfo, IWebhookUrls } from '../../models/i-payment-models';
import { PaymentStorageService } from '../../services/payment-storage.service';
import { PAYMENT_PERSON_TYPES, PAYMENT_PLATFORM_TYPES } from '../../payment.constants';
import { PaymentApiService } from '../../../api-services/services/payment-api.service';

@Component({
  standalone: false,
  selector: 'zero-payment-b',
  templateUrl: './zero-payment-b.component.html',
  styleUrls: ['./zero-payment-b.component.scss'],
})
export class ZeroPaymentBComponent implements OnChanges, OnInit {
  @Input() public personType: string;
  @Input() public countryCode: string;
  @Input() public user: IUser;
  @Input() public userCompany: IUserCompany;
  @Input() bakeryId: number = null;

  @Input() public webhookUrls: IWebhookUrls;
  @Input() public isSharedBusinessAccount = false;

  @Input() public showHeader = true;

  @Output() onCompanySelected = new EventEmitter<IUserCompany>();

  @Output() public onSuccess = new EventEmitter<string>();
  @Output() public onFail = new EventEmitter<any>();
  @Output() public onTermsClick = new EventEmitter<void>();
  @Output() onBusy = new EventEmitter<Promise<any>>();

  public PAYMENT_PERSON_TYPES = PAYMENT_PERSON_TYPES;
  public isContactInfoValid = false;
  public contactInfo: IPaymentInfo;
  public invoiceInfo: IInvoiceInfo;

  public alreadySubscriber = false;

  constructor(
    private _paymentApi: PaymentApiService,
    private _paymentStorage: PaymentStorageService,
  ) {}

  ngOnInit() {
    const { zeroPayment } = PAYMENT_PLATFORM_TYPES;

    const invoiceInfo = this._paymentStorage.getInvoiceInfo();
    const contactInfo = this._paymentStorage.getContactInfo(zeroPayment) || this.contactInfo || {};

    if (
      (invoiceInfo && contactInfo.invoiceCompanyId !== invoiceInfo.company.id) ||
      (this.userCompany && contactInfo.invoiceCompanyId !== this.userCompany.id)
    ) {
      const user = { ...this.user};
      if (!this.user) {
        const { userName, userEmail, userPhone } = contactInfo;
        user.fullName = userName;
        user.email = userEmail;
        user.phone = userPhone;
      }
      const invoiceData = invoiceInfo || this.getInfoFromBakeryInvoiceCompany(this.userCompany);
      const legalCompany = invoiceData && invoiceData.company && invoiceData.company.legalCompany;
      const modifiedInfo: IPaymentInfo = {
        ...contactInfo,
        userCompanyName: legalCompany.name,
        invoiceCompanyId: invoiceData.company.id,
        invoiceCompanyOrgNumber: legalCompany.orgNumber,
        invoiceCompanyDiscount: invoiceData.company.discount,
        invoiceCompanyCountry: legalCompany.country,
      };
      this._paymentStorage.updateContactInfo(zeroPayment, modifiedInfo, user);
      this.contactInfo = this._paymentStorage.getContactInfo(zeroPayment);
    }
  }

  private getInfoFromBakeryInvoiceCompany(companyInfo: IUserCompany): IInvoiceInfo {
    return {
      company: companyInfo,
      orgNumber: companyInfo.legalCompany.orgNumber,
      orgName: companyInfo.legalCompany.name,
      contactPersonName: '',
    };
  }

  ngOnChanges(changes: SimpleChanges) {
    ngHelper.ifChanged(changes, 'user', (user: IUser, prevUser: IUser) => {
      const { zeroPayment } = PAYMENT_PLATFORM_TYPES;

      this.alreadySubscriber = user && user.subscribedOnNews;

      if (user && this.isSharedBusinessAccount && !prevUser) {
        this._paymentStorage.updateContactInfo(zeroPayment, null, user);
      }

      const prevInfo = this.getLastPaymentUserInfo();
      const info = this._paymentStorage.getContactInfo(zeroPayment);
      this.contactInfo = info || prevInfo;
    });
  }

  async submit() {
    const payload = this.getPaymentPayload(this.contactInfo);
    try {
      const data$ = firstValueFrom(this._paymentApi.createZeroPayment(payload));
      this.onBusy.emit(data$);

      const { sid } = await data$;
      this.onSuccess.emit(sid);
    } catch (err) {
      this.onFail.emit(err);
    }
  }

  getPaymentPayload(info: IPaymentInfo) {
    const { PRIVATE } = PAYMENT_PERSON_TYPES;
    const { userCompanyName, ...payload } = { ...info };

    return {
      ...payload,
      ...this.webhookUrls,
      ...(this.personType !== PRIVATE && { userCompanyName }),
    };
  }

  onInfoUpdated(paymentInfo: IPaymentInfo) {
    const { zeroPayment } = PAYMENT_PLATFORM_TYPES;
    this.contactInfo = { ...this.contactInfo, ...paymentInfo };
    this._paymentStorage.updateContactInfo(zeroPayment, this.contactInfo);
  }

  private getLastPaymentUserInfo() {
    const { invoice, bambora, directVipps, payInStore, giftCard } = PAYMENT_PLATFORM_TYPES;
    const possiblePayments = [invoice, bambora, directVipps, payInStore, giftCard];
    const lastPayment = possiblePayments.find((payment) => !!this._paymentStorage.getContactInfo(payment));
    return lastPayment && this._paymentStorage.getContactInfo(lastPayment);
  }
}
